<route lang="json">
{ "name": "fashion-fix" }
</route>

<template>
  <div v-if="user.isAuthenticated && user.isFashionFix">
    <DebugBlock>
      <Button @click="leaveFashionfix">Leave Fashionfix</Button>
    </DebugBlock>

    <FashionFixSignedIn />
  </div>

  <div v-else>
    <DebugBlock>
      <Button @click="joinFashionfix">Join Fashionfix</Button>
    </DebugBlock>

    <div class="fashion-fix-container">
      <h2 class="top-header">What is the Fashion Fix?</h2>
      <div class="description-text">
        <p>
          Paparazzi has revolutionized the way people accessorize, proving that
          you don’t need to break the bank to look like a million bucks. And
          with hot, new, trendy designs arriving regularly, it’s easier than
          ever to expand your horizons and discover new looks and styles that
          empower you.
        </p>
        <p>
          The Paparazzi Fashion Fix makes stylizing effortless: taking the
          guesswork out of accessorizing with its perfectly coordinated Trend
          Blends, and essentially putting the Paparazzi Stylists in your back
          pocket.
        </p>
        <p>
          The Fashion Fix is a monthly subscription, exclusively available to
          Paparazzi Consultants. Each delivery contains 25 new accessories,
          including five Trend Blends (four pieces each) from each of the
          following Paparazzi Collections: Fiercely 5th Avenue, Glimpses of
          Malibu, Magnificent Musings, Simply Santa Fe, and Sunset Sightings.
          Every month, you’ll also receive five exclusive Stylist Picks that you
          can’t get anywhere other than the Fashion Fix!
        </p>
      </div>

      <h2 class="top-header">What is a Trend Blend?</h2>
      <div class="description-text">
        <p>
          Mixing and matching accessories to find the perfect combination can be
          intimidating. Which textures work together? What colors complement
          each other? Our stylist-curated Trend Blends are the solution to this
          dilemma!
        </p>
        <p>
          A Trend Blend is a coordinated set of four accessories. The Trend
          Blends included in the Fashion Fix are perfectly stylized within the
          Paparazzi Collections, offering a range of looks. From the timeless
          sophistication of the Fiercely 5th Avenue Collection to the
          unapologetic individualism of the Sunset Sightings Collection, each
          Fashion Fix Trend Blend is designed to make accessorizing stress-free,
          so you can keep the focus on being fabulous.
        </p>
      </div>

      <div class="image-and-text">
        <div class="group-image-section">
          <img
            v-bind="Header[0]"
            sizes="546px"
            class="group-image"
            alt="Friends sitting on a couch discussing paparazzi accessories fashion fix subscription service"
          />
        </div>
        <div class="group-text">
          <h3>Stylist Expertise at Your Fingertips</h3>
          <div class="explanation-container">
            <p>
              As a Consultant, your customers look to you for their
              accessorizing needs, whether that be a flawless finishing touch
              for a special occasion, a gift for someone special, or help in
              stepping out of their comfort zone to explore a new trend.
            </p>
            <p>
              These requests can cause panic and self-doubt to seep into the
              hearts of even the trendiest Consultants. But the Fashion Fix
              offers a solution!
            </p>
            <p>
              The Fashion Fix empowers you as a Consultant, as every Trend Blend
              comes with access to an accompanying Style Guide. This Style Guide
              gives you the breakdown on why our team of stylists paired the
              accessories together, talking points to help you sell the Trend
              Blends to your customers, tips on what to wear the jewelry with,
              and even suggested Pantone® pairings to complement the look. This
              puts the Paparazzi Stylists in your back pocket and makes you look
              like an expert!
            </p>
            <p>
              You can be a trendsetter all while learning tips and tricks that
              will boost your sales as a Consultant. How’s that for fabulous?
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="inset-section">
      <div class="background-rectangle"></div>

      <div class="inset-text">
        <div class="line"></div>
        <div class="padding-round">
          <h3>How Does it Work?</h3>
          <div>
            <p>
              As a subscriber, you’ll receive your Fashion Fix the last week of
              each month, giving you the inside scoop on the newest trends that
              are about to hit the Paparazzi website. Invite your customers to
              check out the newest arrivals and dazzle them with your expertise
              by sharing the stylist tips and talking points we’ve provided.
            </p>
            <p>
              On the 5th of the following month, the accessories your customers
              have fallen in love with will be available online. Whether they
              shop directly from your replicated website, or you place an order
              and fulfill their requests through your own inventory system,
              there’s no doubt that those loyal customers will be back for more.
              That’s because only YOU – the Fashion Fix subscriber - will have
              exclusive access to get them these coveted designs!
            </p>
            <p>
              On the 20th of the month, your next Fashion Fix will process, and
              any remaining pieces from the current month’s Trend Blends — if
              there are any — will be made available to the entire Paparazzi
              family.
            </p>
            <p class="last">
              Fashion Fix at a Glance: 25 NEW accessories every month,
              including:
            </p>
            <ul class="dashed">
              <li>5 sets of four coordinating accessories known as Trend Blends</li>
              <li>5 exclusive Stylist Picks you can’t get anywhere else</li>
              <li>Style Guides for each Trend Blend, complete with tips and talking points from the Paparazzi Stylists.</li>
              <li>Subscription billed on the 20th of each month. Exclusive access to purchase the Trend Blends on the 5th of the following month.</li>
              <li>Cancel anytime.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="pink-bar">
      <p>Enroll Today!</p>
      <p>
        <strong
          >A limited number of Fashion Fix subscriptions are available.</strong
        >
      </p>
      <p>
        Please contact Paparazzi Support at (855) 697-2727 to be added to the
        waiting list.
      </p>
    </div>
    <div v-if="typeof collectionImages !== 'string'" class="collections">
      <h2>Collections</h2>
      <div v-for="collection of collections" class="collection">
        <img
          v-if="collection.image"
          :src="collection.image"
          sizes="262px"
          :alt="collection.name"
        />
        <div class="collection-text">
          <div class="name">{{ collection.name }}</div>
          <div class="desc">{{ collection.description.description }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Header from "@/assets/img/fashion-fix/non_signed_in_header.jpeg?preset=thumbnail";
import DebugBlock from "@/components/DebugBlock.vue";
import FashionFixSignedIn from "@/components/FashionFixSignedIn.vue";
import { useUserStore } from "@/stores/user";
import { useHead } from "@unhead/vue";
import api from "@virgodev/bazaar/functions/api";
import Button from "primevue/button";
import { computed, onMounted, ref } from "vue";

const user = useUserStore();

const collectionImages = ref<
  "loading" | Record<string, { image: string | null }>
>("loading");
const collections = computed(() => {
  if (typeof collectionImages.value === "object") {
    return [
      {
        image: collectionImages.value["Fiercely 5th Avenue"]?.image,
        name: "Fiercely 5th Avenue",
        description: collection_descriptions[0],
      },
      {
        image: collectionImages.value["Glimpses of Malibu"]?.image,
        name: "Glimpses of Malibu",
        description: collection_descriptions[1],
      },
      {
        image: collectionImages.value["Magnificent Musings"]?.image,
        name: "Magnificent Musings",
        description: collection_descriptions[2],
      },
      {
        image: collectionImages.value["Simply Santa Fe"]?.image,
        name: "Simply Santa Fe",
        description: collection_descriptions[3],
      },
      {
        image: collectionImages.value["Sunset Sightings"]?.image,
        name: "Sunset Sightings",
        description: collection_descriptions[4],
      },
    ];
  } else {
    return [];
  }
});

const collection_descriptions = [
  {
    id: 1,
    description:
      "Timeless and classic yet sophisticated and versatile, the Fiercely 5th Avenue Collection features elegant designs and traditional metal finishes. Never one to shy away from a bit of sparkle, the Fiercely 5th Avenue fan is a powerhouse professional who accessorizes strategically – creating a polished look for the office that easily transitions to an elegantly glamorous after-hours style.",
  },
  {
    id: 2,
    description:
      "Featuring playful styles accented with fun pops of color, the Glimpses of Malibu Collection is an endearing assortment of cheerful and whimsical laid-back fashion. From the bubbly outgoing socialite who isn’t afraid to make a fashion statement to the introspective wallflower who is charmed by soft feminine prints and flawless finishing touches, there’s something for everyone in the Glimpses of Malibu Collection.",
  },
  {
    id: 3,
    description:
      "The Magnificent Musings Collection features bold statement pieces and edgy designs. Reveling in sassy, unapologetic fashion, Magnificent Musings mavens confidently express themselves through glamorously grungy styles and smoldering looks. Mysterious and unpredictable, the Magnificent Musings Collection was made for the self-assured individual who artfully invites their admirers to do a double take when they walk into a room.",
  },
  {
    id: 4,
    description:
      "Authentic designs and handcrafted pieces are staples of the Simply Santa Fe Collection. Featuring patterns and elements inspired by nature, these earthy designs tend to have a little more detail. Those free spirited collectors who have an appreciation for the creative process and delight in the sentimental stories behind their favorite accessories will feel right at home with the Simply Santa Fe Collection.",
  },
  {
    id: 5,
    description:
      "Fueled by abstract designs and funky combinations of the most daring trends, the Sunset Sightings Collection features audacious, outside-the-box fashion. Sunset Sightings fashionistas celebrate individuality, pulling ideas and inspiration from runway shows and online influencers. This collection was made for those who thrive on pushing the envelope and their bold fashion choices are usually met with envious onlookers saying, “I wish I could pull that off!”",
  },
];

useHead({
  title: "Fashion Fix: Monthly Jewelry Subscription",
  meta: [
    {
      name: "description",
      content:
        "Monthly style made easy with Fashion Fix - Trendy, affordable accessories for Paparazzi Consultants.",
    },
  ],
  link: [
    {
      rel: "canonical",
      href: "https://paparazziaccessories.com/fashion-fix",
    },
  ],
});

onMounted(() => {
  getCollectionImages();
});

async function getCollectionImages() {
  const response = await api({ url: "non_sign_in_images/landing_page/" });

  if (response.ok) {
    collectionImages.value = response.body;
  }
}

function joinFashionfix() {
  user.props.groups.push("fashionfix");
}

function leaveFashionfix() {
  const index = user.props.groups.indexOf("fashionfix");
  user.props.groups.splice(index, 1);
}
</script>

<style scoped>
/* Global */

.page {
  margin-bottom: var(--1XL, 64px);
}
.top-header {
  margin-top: 0;
}
h2,
h3 {
  font-family: Indivisible;
  font-size: 24px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  text-transform: uppercase;
  text-align: start;

  margin-bottom: 16px;
}
h2 {
  margin-top: var(--1XL, 64px);
}
p {
  font-family: Indivisible;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.fashion-fix-container {
  max-width: 1020px;
  margin: 0 auto;
}
.description-text {
  margin-bottom: 40px;
  p {
    margin: 0 0 var(--XS, 16px) 0;
  }
}
.group-image {
  object-fit: cover;
  width: 100%;
  height: 100%;
  object-position: center top;
}
.image-and-text {
  margin-bottom: var(--1XL, 64px);
}
.inset-section {
  position: relative;
  background: #ededed;

  .background-rectangle {
    position: absolute;
    width: 215px;
    background-color: white;
    z-index: 1;
  }

  .inset-text {
    position: relative;
    z-index: 10;
    display: flex;
    margin-bottom: 32px;
    .padding-round {
      padding: 32px 32px 0;
    }
    h3 {
      margin-top: 0;
    }
    p {
      margin-top: 0;
    }
    p.last {
      margin: 0;
    }
    ul {
      margin: 0;
      font-size: 14px;
      padding-left: 20px;
    }
    ul.dashed {
      list-style-type: none;
    }
    ul.dashed > li {
      text-indent: -5px;
      text-align: left;
    }
    ul.dashed > li:before {
      content: "-";
      text-indent: -5px;
      padding-right: 5px;
    }
  }
}
.line {
  width: 1px;
  background-color: black;
  margin-top: 32px;
  margin-left: 39px;
}
.pink-bar {
  padding: 24px 48px;
  background-color: #ec008c;

  p {
    color: white;
    font-family: Indivisible;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    margin-bottom: 0px;
    margin-top: 0px;
  }
}
.collections {
  background-color: #ededed;

  .name {
    margin-bottom: 16px;

    font-family: Indivisible;
    font-size: 24px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
    text-transform: uppercase;
  }

  .desc {
    font-family: Indivisible;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
}

/* Mobile */
@media (max-width: 1120px) {
  .fashion-fix-container {
    padding: 0 var(--Sm, 24px);
  }
}
@media (max-width: 1119px) and (min-width: 901px) {
  .explanation-container {
    height: 400px;
  }
  .image-and-text img {
    width: 520px !important;
    height: 100% !important;
  }
}

@media (max-width: 800px) {
  .group-image-section {
    height: 400px;
    margin-bottom: 24px;
  }

  .inset-section {
    padding: var(--lg, 48px);
    /* margin-left: -24px; */
    width: 100vw;
  }
  .background-rectangle {
    height: calc(100% - 96px);
  }
  .pink-bar {
    margin-bottom: 104px;
    /* margin-left: -24px; */
    width: 100vw;
  }
  .collections {
    position: relative;
    width: 100vw;
    /* margin-left: -24px; */
    padding: 96px 24px;

    h2 {
      text-align: center;
      font-family: Indivisible;
      font-size: 44px;
      font-style: normal;
      font-weight: 300;
      line-height: normal;
      margin: -125px auto 72px;
    }

    .collection {
      display: flex;
      flex-direction: column;
      align-items: center;
      background-color: white;
      padding: 0px 24px 48px 24px;
      margin-bottom: 96px;
      img {
        width: 262px;
        height: 327px;
        margin-top: -40px;
        margin-bottom: 40px;
      }
    }
    .collection:last-child {
      margin-bottom: 0px;
    }
  }
  .fashion-fix-container {
    padding: 0;
  }
  .inset-section,
  .pink-bar,
  .collections {
    margin-left: -24px;
  }
  .inset-text {
    .padding-round {
      padding: 32px 0 0 32px !important;
    }
  }
}

/* Desktop */
@media (min-width: 1120px) {
  .explanation-container {
    height: 275px;
  }
}
@media (min-width: 801px) {
  .image-and-text {
    display: flex;

    img {
      width: 520px;
      height: 100%;
    }

    .group-text {
      margin-left: var(--Sm, 24px);
    }

    h3 {
      margin-top: 0px;
    }

    .explanation-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      p {
        margin: 0px;
        margin-bottom: 16px;
      }
    }
  }

  .inset-section {
    padding: var(--lg, 48px);

    .background-rectangle {
      height: calc(100% - 96px);
    }
  }

  .pink-bar {
    margin-bottom: 104px;
  }

  .collections {
    padding: 0px 64px 64px;

    h2 {
      text-align: center;
      font-kerning: none;
      font-family: Indivisible;
      font-size: 100px;
      font-style: normal;
      font-weight: 300;
      line-height: normal;

      transform: translateY(-54%);
    }

    .collection {
      position: relative;
      display: flex;
      justify-content: space-around;
      align-items: center;

      background-color: white;
      margin-bottom: 126px;
      padding-inline: 56px;
      padding-block: 56px;

      min-height: 248px;
      img {
        position: absolute;
        left: 56px;

        margin-right: 40px;
        width: 262px;
        height: 327.5px;
        object-fit: cover;
      }

      .collection-text {
        margin-left: 302px;
      }
    }
    .collection:last-child {
      margin-bottom: 62px;
    }
  }
}

@media (max-width: 900px) and (min-width: 801px) {
  .collections {
    .collection {
      padding-block: 20px;
      padding-inline: 56px 40px;
      .name {
        letter-spacing: -1px;
      }
    }
  }
}

@media (max-width: 601px) {
  .group-image-section {
    height: 300px;
  }
  .pink-bar {
    margin-bottom: 90px;
  }
  .inset-section {
    padding: var(--lg, 48px) var(--Sm, 24px) var(--lg, 48px) var(--lg, 48px);
    :deep(.line) {
      margin-left: 30px;
      width: 2px;
    }
    .inset-text {
      .padding-round {
        padding: 28px 28px 0 !important;
      }
    }
  }
}
@media (max-width: 501px) {
  .group-image-section {
    height: 230px;
    overflow: hidden;
  }
}

@media (max-width: 401px) {
  .collections {
    h2 {
      font-size: 40px;
      margin: -123px auto 67px;
    }
  }
}
@media (max-width: 365px) {
  .line {
    margin-left: 24px;
  }
  .inset-section {
    .inset-text {
      h3 {
        letter-spacing: -1px;
      }
      .padding-round {
        padding: 28px 0 0 24px !important;
      }
    }
  }
  .collections {
    h2 {
      font-size: 30px;
      margin: -115px auto 67px;
    }
    .name {
      letter-spacing: -1px;
    }
    .collection {
      img {
        max-width: 262px;
        max-height: 327px;
        height: auto;
        width: 100vw;
      }
    }
  }
}

@media (max-width: 325px) {
  .inset-section {
    padding: var(--lg, 48px) var(--Sm, 24px);
    .background-rectangle {
      width: 65vw;
    }
  }
}

@media (max-width: 294px) {
  .line {
    display: none;
  }
  .pink-bar {
    margin-bottom: 55px;
  }
  .collections {
    h2 {
      font-size: 24px;
      margin: -112px auto 67px;
    }
  }
}
</style>
